import { useMemo } from 'react';

import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useSelector } from 'react-redux';

import type {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- 'DevicePreferences' is defined but never used.
  PreferenceUpdateBody,
  PreferencesResponse,
} from '../@types/preferences';
import { confidenceScoreFilterAtom } from '../modules/annotator/findingSidebar/confidenceScoreFilter/atoms';
import { makeAPIRequestV2 } from '../utils/api';
import { StoreKeys, getLocal } from '../utils/hooks/useLocalStorage';

import { makeAPIRequestReactQuery } from './api';
import { endpoints } from './endpoints';

export const usePreferences = (isOrgLevel?: boolean) => {
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector((state: any) => state.login.user);

  const [confidenceScoreFilter, setConfidenceScoreFilter] = useAtom(
    confidenceScoreFilterAtom,
  );
  const localStorageOverrides = getLocal<Record<string, any>>(
    StoreKeys.PREFERENCES_OVERRIDES_KEY,
    {},
  );
  const path = `/${endpoints.preferences}`;

  const result = useQuery<PreferencesResponse>(
    [endpoints.preferences, currentPractice, currentOrg],
    {
      queryFn: () => {
        return makeAPIRequestReactQuery<PreferencesResponse>(path, {
          organizationId: currentOrg,
          practiceId: currentPractice,
        });
      },
      cacheTime: 1 * 60 * 1000,
      staleTime: 1 * 60 * 1000,
      enabled: isOrgLevel
        ? Boolean(currentOrg)
        : Boolean(currentPractice && currentOrg),
      onSuccess(data) {
        if (data.confidenceThreshold && !confidenceScoreFilter) {
          setConfidenceScoreFilter(data.confidenceThreshold);
        }
      },
    },
  );

  const dataWithOverrides = useMemo(
    () =>
      result.data ? { ...result.data, ...localStorageOverrides } : result.data,
    [localStorageOverrides, result.data],
  );

  return {
    ...result,
    data: {
      ...dataWithOverrides,
      // cleanClaims: false,
      // claimAttachmentPdfEnabled: false,
      // isFeedbackWidgetEnabled: true,
      // isInsuranceEligibilityEnabled: true,
      newImageAnnotatorThumbnailsForClaims: true,
    },
    serverData: result.data,
  };
};

export const usePreferencesUserUpdate = (
  options?: Omit<
    UseMutationOptions<
      PreferenceUpdateBody,
      unknown,
      PreferenceUpdateBody,
      { previousPreferences: PreferencesResponse | undefined }
    >,
    'mutationFn'
  >,
  shouldInvalidateFmxSummary?: boolean,
) => {
  const user = useSelector((state: any) => state.login.user);
  const currentOrg = user.primaryOrganizationId;
  const practiceId = user.primaryPracticeId;
  const queryClient = useQueryClient();

  return useMutation<
    PreferenceUpdateBody,
    unknown,
    PreferenceUpdateBody,
    { previousPreferences: PreferencesResponse | undefined }
  >(
    async (update: PreferenceUpdateBody) => {
      await queryClient.cancelQueries([
        endpoints.preferences,
        practiceId,
        currentOrg,
      ]);

      queryClient.setQueryData<PreferencesResponse>(
        [endpoints.preferences, practiceId, currentOrg],
        (old) => {
          return old ? { ...old, ...update } : old;
        },
      );
      await makeAPIRequestV2(
        `/${endpoints.preferences}/user`,
        { data: { preferenceValue: update } },
        'POST',
      );
      return update;
    },
    {
      ...options,
      onError: (err, newPreferences, context) => {
        queryClient.setQueryData<PreferencesResponse>(
          [endpoints.preferences, practiceId, currentOrg],
          context?.previousPreferences,
        );
        options?.onError?.(err, newPreferences, context);
      },
      onSettled: (data, error, variables, context) => {
        queryClient.invalidateQueries([endpoints.preferences]);
        options?.onSettled?.(data, error, variables, context);
      },
      onSuccess: (data, variables, context) => {
        if (shouldInvalidateFmxSummary) {
          queryClient.invalidateQueries([endpoints.fmxSummaries]);
          queryClient.invalidateQueries([endpoints.fmxSummary]);
        }
        queryClient.invalidateQueries([endpoints.appointments]);
        queryClient.invalidateQueries([endpoints.tasks]);
        options?.onSuccess?.(data, variables, context);
      },
    },
  );
};

export const usePreferencesPracticeUpdate = (
  options?: Omit<
    UseMutationOptions<
      PreferenceUpdateBody,
      unknown,
      PreferenceUpdateBody,
      { previousPreferences: PreferencesResponse | undefined }
    >,
    'mutationFn'
  >,
  shouldInvalidateFmxSummary?: boolean,
) => {
  const user = useSelector((state: any) => state.login.user);

  const practiceId = user.primaryPracticeId;
  const currentOrg = user.primaryOrganizationId;
  const queryClient = useQueryClient();

  return useMutation<
    PreferenceUpdateBody,
    unknown,
    PreferenceUpdateBody,
    { previousPreferences: PreferencesResponse | undefined }
  >(
    async (update: PreferenceUpdateBody) => {
      await queryClient.cancelQueries([
        endpoints.preferences,
        practiceId,
        currentOrg,
      ]);

      queryClient.setQueryData<PreferencesResponse>(
        [endpoints.preferences, practiceId, currentOrg],
        (old) => {
          return old ? { ...old, ...update } : old;
        },
      );
      await makeAPIRequestV2(
        `/${endpoints.preferences}/practice`,
        { data: { practiceId, preferenceValue: update } },
        'POST',
      );
      return update;
    },
    {
      ...options,
      onError: (err, newPreferences, context) => {
        queryClient.setQueryData<PreferencesResponse>(
          [endpoints.preferences, practiceId, currentOrg],
          context?.previousPreferences,
        );
        options?.onError?.(err, newPreferences, context);
      },
      onSettled: (data, error, variables, context) => {
        queryClient.invalidateQueries([endpoints.preferences]);
        options?.onSettled?.(data, error, variables, context);
      },
      onSuccess: (data, variables, context) => {
        if (shouldInvalidateFmxSummary) {
          queryClient.invalidateQueries([endpoints.fmxSummaries]);
          queryClient.invalidateQueries([endpoints.fmxSummary]);
        }
        queryClient.invalidateQueries([endpoints.appointments]);
        queryClient.invalidateQueries([endpoints.tasks]);
        options?.onSuccess?.(data, variables, context);
      },
    },
  );
};

export const usePreferencesOrgUpdate = (
  options?: Omit<
    UseMutationOptions<
      PreferenceUpdateBody,
      unknown,
      PreferenceUpdateBody,
      { previousPreferences: PreferencesResponse | undefined }
    >,
    'mutationFn'
  >,
) => {
  const user = useSelector((state: any) => state.login.user);
  const currentOrg = user.primaryOrganizationId;
  const queryClient = useQueryClient();

  return useMutation<
    PreferenceUpdateBody,
    unknown,
    PreferenceUpdateBody,
    { previousPreferences: PreferencesResponse | undefined }
  >(
    async (update: PreferenceUpdateBody) => {
      // need to cancel and update all pref queries for this org
      const allKeys = queryClient.getQueryCache().findAll({
        predicate: (query) =>
          query.queryKey[0] === endpoints.preferences &&
          query.queryKey[2] === currentOrg,
      });

      allKeys.map(async (queryKey) => {
        queryClient.cancelQueries(queryKey.queryKey);

        queryClient.setQueryData<PreferencesResponse>(
          [endpoints.preferences, undefined, currentOrg],
          (old) => {
            return update ? { ...old, ...update } : old;
          },
        );
      });

      // make the backend request to update org prefs
      await makeAPIRequestV2(
        `/${endpoints.preferences}/organization`,
        { data: { organizationId: currentOrg, preferenceValue: update } },
        'POST',
      );
      return update;
    },
    {
      ...options,
      onError: (err, newPreferences, context) => {
        const allKeys = queryClient.getQueryCache().findAll({
          predicate: (query) =>
            query.queryKey[0] === endpoints.preferences &&
            query.queryKey[2] === currentOrg,
        });
        allKeys.map((queryKey) => {
          queryClient.setQueryData<PreferencesResponse>(
            queryKey.queryKey,
            context?.previousPreferences,
          );
        });
        options?.onError?.(err, newPreferences, context);
      },
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries([endpoints.preferences]);
        options?.onSuccess?.(data, variables, context);
      },
    },
  );
};
